import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useQuery, useMutation } from 'react-query';
import { getGenerativeFileDetails, putGenerativeLabels } from 'entities/generative-models/api';
import { getErrorMessage } from 'shared/helpers/getErrorMessage';
import { Button } from 'shared/ui/Button';
import { Loader } from 'shared/ui/Loader';
import { EFHeader } from 'widgets/editFile/Header';

import { useFileEditorStore } from 'store/file-editor';
import DocumentViewer from './partials/document-viewer';
import FileDetails from './partials/file-details';
import GenerativeResearch from './partials/generative-research';

const FileEdior = () => {
	const { t } = useTranslation();
	const { fileId } = useParams();
	const navigate = useNavigate();
	const { fileDetails, setFileDetails } = useFileEditorStore();

	const { isLoading: getIsLoading } = useQuery({
		queryKey: ['get/generative-model/file-details'],
		queryFn: () => getGenerativeFileDetails({ generativeModelFileId: fileId || '' }),
		onSuccess: (response) => {
			setFileDetails(response.data);
		},
		onError: (err) => {
			const errorMessage = getErrorMessage(err);
			enqueueSnackbar({ message: errorMessage, variant: 'error' });
		},
	});

	const { mutate: putGenerativeLabelsMutate, isLoading: putIsLoading } = useMutation({
		mutationKey: ['put/generative-file-labels'],
		mutationFn: putGenerativeLabels,

		onError: (err) => {
			const errorMessage = getErrorMessage(err);
			enqueueSnackbar({ message: errorMessage, variant: 'error' });
		},
	});

	const onSaveHandler = () => {
		if (fileId && fileDetails) {
			putGenerativeLabelsMutate(
				{
					generativeModelFileId: fileId,
					data: { labels: fileDetails.labels, execute: false },
				},
				{
					onSuccess: () => {
						navigate(-1);
					},
				},
			);
		}
	};
	const onRunHandler = () => {
		if (fileId && fileDetails) {
			putGenerativeLabelsMutate(
				{
					generativeModelFileId: fileId,
					data: { labels: fileDetails.labels, execute: true },
				},
				{
					onSuccess: (resposne) => {
						setFileDetails(resposne.data);
					},
				},
			);
		}
	};
	const onCancelHandler = () => {
		navigate(-1);
	};

	if (getIsLoading) {
		return <Loader />;
	}

	return (
		<>
			<EFHeader fileName={fileDetails?.fileName}>
				<Box sx={{ display: 'flex', gap: 2 }}>
					<Button size="small" onClick={onCancelHandler} variant="outlined" disabled={putIsLoading}>
						{t('shared_texts.cancel')}
					</Button>
					<Button size="small" onClick={onRunHandler} loading={putIsLoading}>
						{t('shared_texts.run')}
					</Button>
					<Button size="small" onClick={onSaveHandler} loading={putIsLoading}>
						{t('shared_texts.save')}
					</Button>
				</Box>
			</EFHeader>

			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'space-between',
					gap: '20px',
				}}
			>
				<Box
					sx={{
						'position': 'sticky',
						'top': '20px',
						'width': '60%',
						'display': 'flex',
						'flexDirection': 'column',
						'flexGrow': '2',
						'@media screen and (min-width: 2561px)': {
							flexGrow: '4',
							width: '80%',
						},
					}}
				>
					<DocumentViewer />
				</Box>

				<Box
					sx={{
						'display': 'flex',
						'flexDirection': 'column',
						'gap': '20px',
						'flexGrow': '1',
						'width': '30%',
						'@media screen and (min-width: 1441px)': {
							width: '15%',
						},
					}}
				>
					<FileDetails />
					<GenerativeResearch />
				</Box>
			</Box>
		</>
	);
};

export default FileEdior;
